import { useEffect } from 'react';
import { onAuthStateChanged, getRedirectResult } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, collection, getDocs } from 'firebase/firestore';
import { auth, db, isFirebaseConfigured } from '../firebase/config';
import { useAuthStore } from '../store/authStore';
import { useChildStore } from '../store/childStore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadProgress, loadBadges } from '../firebase/persistence';
import type { User, Child } from '../types';
import { PREBUILT_PATHWAYS } from '../data/pathways';
import { logError, logWarn } from '../utils/errorLogger';

const INVITE_CODE_KEY = 'goodatoms_pending_invite';

/** Rejects after `ms` ms — prevents Firestore from hanging the auth listener */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`Firestore timeout after ${ms}ms`)), ms)
    ),
  ]);
}

/**
 * Makes `child` the active child AND loads that child's progress + badges from
 * Firestore. Switching must always do both — otherwise the previous child's
 * streaks and badges keep showing under the new child's name.
 */
export async function switchToChild(uid: string | undefined, child: Child): Promise<void> {
  const { setActiveChild, setBulkProgress, setBadges } = useChildStore.getState();
  setActiveChild(child);
  // Progress is a flat pathway-keyed record shared across children — it must be
  // cleared on switch or the previous child's streak renders under the new child's name.
  setBulkProgress({});
  setBadges([], []);
  if (!uid || !isFirebaseConfigured()) return;
  const [progressData, badgeData] = await Promise.all([
    loadProgress(uid, child.id),
    loadBadges(uid, child.id),
  ]);
  setBulkProgress(progressData);
  setBadges(badgeData.badges, badgeData.lessonBadges);
}

export function useAuthListener() {
  useEffect(() => {
    // Access store setters via getState() to avoid dependency array issues
    const { setUser } = useAuthStore.getState();
    const { setChildren, setActiveChild, setBulkProgress, setBadges, setPathways } = useChildStore.getState();

    if (!isFirebaseConfigured()) {
      setUser({
        uid: 'demo-user',
        email: 'demo@goodatoms.com',
        displayName: 'Demo Parent',
        plan: 'premium',
        planExpiresAt: null,
        createdAt: new Date(),
        language: 'no',
      });
      return;
    }

    getRedirectResult(auth).catch((err) => {
      logWarn('useAuth', 'Redirect result error (non-critical)', { action: 'getRedirectResult', error: String(err) });
    });

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setChildren([]);
        setActiveChild(null);
        setUser(null);
        return;
      }

      try {
        const userRef     = doc(db, 'users', firebaseUser.uid);
        const childrenRef = collection(db, 'users', firebaseUser.uid, 'children');

        const [userSnap, childrenSnap] = await Promise.all([
          withTimeout(getDoc(userRef), 6000),
          withTimeout(getDocs(childrenRef), 5000).catch(() => null),
        ]);

        const children: Child[] = (childrenSnap?.docs ?? []).map((d) => {
          const data = d.data();
          return {
            id:                 d.id,
            name:               data.name               ?? '',
            age:                data.age                ?? 10,
            ageBand:            data.ageBand            ?? '8-12',
            phase:              data.phase              ?? 'builder',
            languagePreference: data.languagePreference ?? 'no',
            activePathwayId:    data.activePathwayId    ?? null,
            interests:          data.interests          ?? [],
            avatar:             data.avatar             ?? 'ninja',
            createdAt:          data.createdAt?.toDate?.()      ?? new Date(),
            lastActiveAt:       data.lastActiveAt?.toDate?.()   ?? new Date(),
            consentGivenAt:     data.consentGivenAt?.toDate?.() ?? new Date(),
          };
        });

        children.sort((a, b) => b.lastActiveAt.getTime() - a.lastActiveAt.getTime());

        // Set child state BEFORE setUser — navigation depends on activeChild being set
        setChildren(children);
        // Full catalog so any remaining store consumers resolve correctly after reload
        setPathways(PREBUILT_PATHWAYS);
        setActiveChild(children[0] ?? null);

        // Load progress + badges for the active child from Firestore
        if (children[0]) {
          const [progressData, badgeData] = await Promise.all([
            loadProgress(firebaseUser.uid, children[0].id),
            loadBadges(firebaseUser.uid, children[0].id),
          ]);
          setBulkProgress(progressData);
          setBadges(badgeData.badges, badgeData.lessonBadges);
        }

        if (userSnap.exists()) {
          const data = userSnap.data();
          setUser({
            uid:          firebaseUser.uid,
            email:        firebaseUser.email,
            displayName:  firebaseUser.displayName,
            plan:         data.plan || 'free',
            planExpiresAt: data.planExpiresAt?.toDate() || null,
            createdAt:    data.createdAt?.toDate() || new Date(),
            language:     data.language || 'no',
          });
        } else {
          const pendingCode = sessionStorage.getItem(INVITE_CODE_KEY) ?? '';
          if (pendingCode) sessionStorage.removeItem(INVITE_CODE_KEY);

          const newUser: Omit<User, 'uid'> = {
            email:         firebaseUser.email,
            displayName:   firebaseUser.displayName,
            plan:          'free',
            planExpiresAt: null,
            createdAt:     new Date(),
            language:      'no',
          };
          await withTimeout(setDoc(userRef, { ...newUser, createdAt: serverTimestamp() }), 6000);
          setUser({ uid: firebaseUser.uid, ...newUser });

          // Redeem invite code server-side (non-blocking — upgrades plan in Firestore)
          if (pendingCode.trim()) {
            try {
              const functions = getFunctions();
              const redeem = httpsCallable<{ code: string }, { plan: string; planExpiresAt: string }>(functions, 'redeemInviteCode');
              const result = await redeem({ code: pendingCode });
              // Update local state with the upgraded plan
              setUser({
                uid: firebaseUser.uid,
                ...newUser,
                plan: result.data.plan as User['plan'],
                planExpiresAt: new Date(result.data.planExpiresAt),
              });
            } catch {
              // Code invalid or already used — user stays on free plan
            }
          }
        }
      } catch (err) {
        logError('useAuth', err, { action: 'onAuthStateChanged', userId: firebaseUser.uid });
        setChildren([]);
        setActiveChild(null);
        setUser({
          uid:          firebaseUser.uid,
          email:        firebaseUser.email,
          displayName:  firebaseUser.displayName,
          plan:         'free',
          planExpiresAt: null,
          createdAt:    new Date(),
          language:     'no',
        });
      }
    });

    return () => unsubscribe();
  }, []);
}
